import { DiagramComponent } from '../../core/DiagramComponent';
import type { SquareConfig, Variables, JXGBoard, JXGElement } from '../../types/interfaces';

interface ExtendedSquareConfig extends SquareConfig {
  strokeWidth?: number;
  attributes?: Record<string, any>;
}

/**
 * Square (rectangle) component
 */
export class Square extends DiagramComponent<ExtendedSquareConfig> {
  constructor(config: ExtendedSquareConfig, board: JXGBoard, parentState: Variables = {}) {
    super(config, board, parentState);
  }

  render(): JXGElement {
    const config = this.config;
    const position = config.position || [0, 0];
    const widthExpr = this.evaluateExpression(config.width || 1);
    const heightExpr = config.height !== undefined 
      ? this.evaluateExpression(config.height) 
      : widthExpr;

    const x = this.evaluateExpression(position[0]);
    const y = this.evaluateExpression(position[1]);

    const numWidth = typeof widthExpr === 'number' ? widthExpr : parseFloat(String(widthExpr)) || 1;
    const numHeight = typeof heightExpr === 'number' ? heightExpr : parseFloat(String(heightExpr)) || numWidth;
    const numX = typeof x === 'number' ? x : parseFloat(String(x)) || 0;
    const numY = typeof y === 'number' ? y : parseFloat(String(y)) || 0;

    // Create polygon (4 corners)
    const points: [number, number][] = [
      [numX, numY],
      [numX + numWidth, numY],
      [numX + numWidth, numY - numHeight],
      [numX, numY - numHeight],
    ];

    const attributes = {
      strokeColor: config.color || '#000',
      strokeWidth: config.strokeWidth || 2,
      fillColor: config.fillColor || 'none',
      fillOpacity: config.fillOpacity || 0.3,
      borders: {
        strokeColor: config.color || '#000',
        strokeWidth: config.strokeWidth || 2,
      },
      vertices: {
        visible: false,
        withLabel: false,
        name: ''
      },
      ...config.attributes,
    };

    const square = this.board.create('polygon', points, attributes);
    this.jsxElements.push(square);

    return square;
  }

  getSpecificity(): number {
    // Specificity inversely proportional to area
    const widthExpr = this.evaluateExpression(this.config.width || 1);
    const heightExpr = this.config.height !== undefined 
      ? this.evaluateExpression(this.config.height) 
      : widthExpr;
    const numWidth = typeof widthExpr === 'number' ? widthExpr : parseFloat(String(widthExpr)) || 1;
    const numHeight = typeof heightExpr === 'number' ? heightExpr : parseFloat(String(heightExpr)) || numWidth;
    const area = numWidth * numHeight;
    return Math.max(10, 100 - area * 10);
  }
}
